/**
 * Знаки в библиографической записи по ГОСТ Р 7.0.100-2018: «. — » между областями,
 * « : » перед издательством, « // » перед сведениями о журнале, «256 с.» и «С. 15–20»,
 * инициалы «И. И.», год издания и точка в конце. Общая часть для правила проверки
 * и для сборки документа из Markdown.
 */

import { NBSP } from '../util/text';

export interface BibIssue {
  start: number;
  end: number;
  /** Замена фрагмента [start, end); нет — исправить автоматически нельзя. */
  replace?: string;
  problem: string;
  /** Нарушены разделители областей или нет года — предупреждение, остальное — совет. */
  major: boolean;
}

export interface BibIssueOptions {
  /** Проверять запись целиком: год, объём, точку в конце. false — только знаки внутри фрагмента текста. */
  entry: boolean;
}

const URL_RE = /(?:https?:\/\/|www\.)\S+/gi;
const YEAR_RE = /(?<!\d)(?:1[5-9]|20)\d{2}(?!\d)/;
const EXTENT_RE = /\d\s?с\.|(?<!\p{L})С\.\s?\d|\d\s?(?:стр|p|pp|S)\.|URL|https?:\/\/|www\.|дата обращения|режим доступа|электронный ресурс/iu;
const NO_EXTENT_RE =
  /закон|кодекс|постановлени|приказ|указ(?!ани)|распоряжени|ГОСТ|СНиП|СанПиН|(?<!\p{L})СП\s|патент|стандарт|конвенци|конституци|(?<!\p{L})(?:ISO|IEC|RFC|IEEE Std)(?!\p{L})/iu;

const PLACE = String.raw`\p{Lu}[\p{L}-]*\.?(?:[\s-]\p{Lu}[\p{L}-]*\.?){0,2}`;

export function bibIssues(text: string, from = 0, opts: BibIssueOptions = { entry: true }): BibIssue[] {
  const out: BibIssue[] = [];
  const urls = [...text.matchAll(URL_RE)].map((m) => [m.index, m.index + m[0].length] as const);
  const inUrl = (a: number, b: number): boolean => urls.some(([s, e]) => a < e && s < b);
  const add = (issue: BibIssue): void => {
    if (issue.start < from || inUrl(issue.start, Math.max(issue.end, issue.start + 1))) return;
    if (out.some((x) => issue.start < Math.max(x.end, x.start + 1) && x.start < Math.max(issue.end, issue.start + 1))) return;
    out.push(issue);
  };
  const scan = (re: RegExp, f: (m: RegExpExecArray) => BibIssue | undefined): void => {
    for (const m of text.matchAll(re)) {
      const issue = f(m as RegExpExecArray);
      if (issue) add(issue);
    }
  };
  const AREA = 'между областями записи ставят «. — »';

  // «2020.—240 с.», «2020. —240 с.»: тире между областями без пробелов.
  scan(/\.([—–])(\s?)/g, (m) => (m[2] ? undefined : { start: m.index, end: m.index + 2, replace: '. — ', problem: AREA, major: true }));
  scan(/\.([—–])(?=\s)/g, (m) => ({ start: m.index, end: m.index + 2, replace: '. —', problem: AREA, major: true }));
  scan(/(?<=\.\s)([—–])(?=[^\s\d–—-])/g, (m) => ({ start: m.index, end: m.index + 1, replace: '— ', problem: AREA, major: true }));
  // «2020.- 240 с.» и «2020.-240 с.»; вариант с пробелами с обеих сторон исправляет правило о дефисах.
  scan(/\.(\s?)(--?|−)(\s?)(?=[\p{L}\d«(])/gu, (m) =>
    m[1] && m[3] ? undefined : { start: m.index, end: m.index + m[0].length, replace: '. — ', problem: AREA, major: true },
  );

  // «Москва: Наука, 2020» → «Москва : Наука, 2020».
  const placeRe = new RegExp(String.raw`(?<=[.—–-]\s?)(${PLACE})(\s*:\s*)(?=[^,:;]{2,80},\s*\[?(?:1[5-9]|20)\d{2})`, 'gu');
  scan(placeRe, (m) => {
    if (m[2] === ' : ') return undefined;
    const start = m.index + m[1]!.length;
    return { start, end: start + m[2]!.length, replace: ' : ', problem: 'перед издательством ставят « : »', major: true };
  });

  // «//Журнал», «// Журнал» без пробела перед знаком.
  scan(/(?<![:/])(\s*)\/\/(\s*)(?!\/)/g, (m) =>
    m[1] === ' ' && m[2] === ' ' ? undefined : { start: m.index, end: m.index + m[0].length, replace: ' // ', problem: 'перед сведениями о журнале или сборнике ставят « // »', major: true },
  );

  // «256 стр.» → «256 с.»; страницы статьи — «С. 15–20».
  scan(/(?<=\d)\s?стр\.?(?![\p{L}])/gu, (m) => ({ start: m.index, end: m.index + m[0].length, replace: `${NBSP}с.`, problem: 'число страниц пишут «256 с.»', major: false }));
  scan(/(?<=[—–]\s)(?:Стр|стр|с)\.\s?(?=\d)/g, (m) => ({ start: m.index, end: m.index + m[0].length, replace: `С.${NBSP}`, problem: 'страницы статьи пишут «С. 15–20»', major: false }));
  scan(/(?<=(?<!\p{L})[Сс]\.\s?\d{1,4})\s?[-−—]\s?(?=\d)/gu, (m) =>
    m[0] === '–' ? undefined : { start: m.index, end: m.index + m[0].length, replace: '–', problem: 'диапазон страниц пишут через тире: «15–20»', major: false },
  );

  // «Иванов И.И.» → «Иванов И. И.».
  scan(/(?<=(?<!\p{L})\p{Lu}\.)(?=\p{Lu}\.)/gu, (m) => ({ start: m.index, end: m.index, replace: NBSP, problem: 'инициалы разделяют пробелом: «И. И.»', major: false }));

  if (opts.entry) {
    const body = text.slice(from);
    const hasUrl = urls.length > 0 || /дата обращения/i.test(body);
    if (!hasUrl && !YEAR_RE.test(body)) out.push({ start: from, end: text.length, problem: 'не указан год издания', major: true });
    if (!EXTENT_RE.test(body) && !NO_EXTENT_RE.test(body) && body.trim().length > 20) {
      out.push({ start: from, end: text.length, problem: 'не указано число страниц книги («256 с.») или страницы статьи («С. 15–20»)', major: false });
    }
    const end = text.trimEnd().length;
    const last = text[end - 1];
    const endsWithFix = out.some((x) => x.end === end && x.replace?.endsWith('.'));
    if (end > from && last && !/[.!?…]/.test(last) && !inUrl(end - 1, end) && !endsWithFix) {
      out.push({ start: end, end, replace: '.', problem: 'запись заканчивается точкой', major: false });
    }
  }
  return out.sort((a, b) => a.start - b.start);
}

/** Текст с исправленными знаками — для сборки документа. */
export function applyBibFixes(text: string, issues: readonly BibIssue[]): string {
  let out = text;
  for (const i of [...issues].sort((a, b) => b.start - a.start)) {
    if (i.replace !== undefined) out = out.slice(0, i.start) + i.replace + out.slice(i.end);
  }
  return out;
}
